import { DOCUMENT, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgbCarousel, NgbSlide } from '@ng-bootstrap/ng-bootstrap';
import { AppImageMultipleQualityComponent } from '@shared/components/app-image-multiple-quality/app-image-multiple-quality.component';
import { ImagePreloadService } from '@shared/services/image-preload.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { Auction } from 'src/app/articles-manager/interfaces/auction.interface';

declare global {
  interface Window {
    CI360: any;
  }
}

@Component({
  selector: 'app-article-gallery',
  templateUrl: './article-gallery.component.html',
  styleUrls: ['./article-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgbCarousel,
    NgIf,
    NgFor,
    NgTemplateOutlet,
    NgbSlide,
    AppImageMultipleQualityComponent,
  ],
})
export class ArticleGalleryComponent implements AfterViewInit {
  selectedAuction = input<Auction>();
  @ViewChild('galleryCarousel') carousel!: NgbCarousel;
  id = signal('id0');
  show360 = signal(false);
  imageWidth = signal(900);
  firstLoaded = signal(false);
  mouseEntered = signal(false);

  photo360 = computed(
    () => this.selectedAuction()?.article?.articlePhoto360List?.length > 0
  );
  frames = computed(
    () => this.selectedAuction()?.article?.articlePhoto360List?.length ?? 1
  );
  mainImage = computed<string | null>(() => {
    if (this.photo360()) {
      return (
        this.selectedAuction().article.articlePhoto360Url +
        this.selectedAuction().article.idArticle +
        '_1.jpg' +
        `?w=${this.imageWidth()}`
      );
    }
    if (this.selectedAuction()?.article?.articlePhotoList[0]?.fullPath) {
      return (
        this.selectedAuction().article.articlePhotoList[0].fullPath +
        `?w=${this.imageWidth()}`
      );
    }

    return null;
  });

  imagePreloadService = inject(ImagePreloadService);
  platformService = inject(PlatformService);
  _change = inject(ChangeDetectorRef);
  private document = inject(DOCUMENT);

  constructor() {
    effect(() => {
      if (this.mainImage()) {
        this.imagePreloadService.preloadImage(this.mainImage());
        this.addPreloadLinkToHead(this.mainImage());
        this.addPreloadLinkToHead(this.mainImage().replace('w=900', 'w=600'));
      }
    });
  }

  /**
   * Adds a preload link to the document head for the main image
   */
  private addPreloadLinkToHead(imageUrl: string): void {
    this.platformService.runOnServer(() => {
      const existingPreload = this.document.querySelector(
        `link[rel="preload"][href="${imageUrl}"]`
      );

      if (!existingPreload) {
        const linkElement = this.document.createElement('link');
        linkElement.rel = 'preload';
        linkElement.href = imageUrl;
        linkElement.as = 'image';
        linkElement.type = 'image/jpeg';

        this.document.head.appendChild(linkElement);
      }

      // Ensure any third-party preloads have a valid `as` value
      const unknownPreloads = Array.from(
        this.document.querySelectorAll('link[rel="preload"]:not([as])')
      ) as HTMLLinkElement[];
      unknownPreloads.forEach((lnk) => {
        try {
          const href = lnk.getAttribute('href') || '';
          if (/\.(jpg|jpeg|png|webp|avif)(\?|$)/i.test(href)) lnk.as = 'image';
          else if (/\.(css)(\?|$)/i.test(href)) lnk.as = 'style';
          else if (/\.(js)(\?|$)/i.test(href)) lnk.as = 'script';
          else if (/\.(woff2?|ttf|otf)(\?|$)/i.test(href)) lnk.as = 'font';
          else lnk.as = 'fetch';
        } catch {
          // do nothing
        }
      });
    });
  }

  ngAfterViewInit() {
    this.platformService.runOnBrowser(() => {
      window.matchMedia('(max-width: 700px)').addEventListener('change', () => {
        this.imageWidth.set(
          window.matchMedia('(max-width: 700px)').matches ? 600 : 900
        );

        this._change.markForCheck();
      });

      this.imageWidth.set(
        window.matchMedia('(max-width: 700px)').matches ? 600 : 900
      );
      this.firstLoaded.set(true);
      this._change.markForCheck();
    });
  }

  public getFullAttachmentPath() {
    return (
      this.selectedAuction().article.idArticle +
      `_{index}.jpg?w=${this.imageWidth()}`
    );
  }

  navigateToSlide(item: string) {
    this.id.set(item);
    this.carousel.select(item);
  }

  mouseEnter() {
    if (!this.mouseEntered()) {
      const viewId = 'cloudimage-360';
      const new360View: any = this.document.getElementById(viewId);
      if (!new360View) return; // safety
      new360View.classList.add('cloudimage-360');
      // Ensure at least the first frame loads before initializing the 360 library to avoid InvalidStateError
      this.ensureFirstFrameLoaded()
        .then((ok) => {
          if (!ok) {
            // Abort enabling 360 view if first frame failed (avoid library crash)
            console.warn('[ArticleGallery] First 360 frame failed to load, skipping CI360 init');
            return false;
          }
          return this.loadCi360().then(() => true);
        })
        .then((ready) => {
          if (!ready) return;
          try {
            (window as any).CI360?.add?.(viewId);
          } catch (err) {
            console.warn('[ArticleGallery] CI360.add error suppressed', err);
            return;
          }
          this._change.markForCheck();
          setTimeout(() => {
            this.show360.set(true);
            this._change.markForCheck();
          }, 100);
          this.mouseEntered.set(true);
        });
    }
  }

  onSlide(e) {
    this.id.set(e.current);
  }

  private ci360Loading = false;
  private ci360Loaded = false;
  private firstFrameChecked = false;

  /**
   * Preloads the first frame of the 360 sequence; returns true if it loads successfully.
   * Protects against initializing CI360 with a broken <img> sequence (causes InvalidStateError internally).
   */
  private ensureFirstFrameLoaded(): Promise<boolean> {
    if (this.firstFrameChecked) return Promise.resolve(true); // already good
    const auction = this.selectedAuction();
    if (!auction?.article?.articlePhoto360Url) return Promise.resolve(false);
    const firstSrc =
      auction.article.articlePhoto360Url +
      auction.article.idArticle +
      '_1.jpg' +
      `?w=${this.imageWidth()}`;

    return new Promise<boolean>((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        cleanup(false);
      }, 7000); // 7s safety

      const cleanup = (ok: boolean) => {
        clearTimeout(timeout);
        img.onload = null;
        img.onerror = null;
        if (ok) this.firstFrameChecked = true;
        resolve(ok);
      };

      img.onload = () => cleanup(true);
      img.onerror = () => cleanup(false);
      img.src = firstSrc;
    });
  }
  private loadCi360(): Promise<void> {
    return new Promise((resolve) => {
      if (this.ci360Loaded || (window as any).CI360) {
        this.ci360Loaded = true;
        resolve();
        return;
      }
      if (this.ci360Loading) {
        const check = () => {
          if ((window as any).CI360) resolve();
          else setTimeout(check, 30);
        };
        check();
        return;
      }
      this.ci360Loading = true;
      const script = this.document.createElement('script');
      script.src = '/assets/js/js-cloudimage-360-view.min.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.ci360Loaded = true;
        resolve();
      };
      script.onerror = () => resolve();
      this.document.body.appendChild(script);
    });
  }
}
